/**
 * Dashboard Page - Activity Selection
 *
 * Picnic/Albert Heijn-style activity grid with daily stats.
 * Shows 7 activities with Route rijden enabled, others coming soon.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore, useBerichtenStore } from '../stores';
import { Card, CardContent, Badge } from '../components/ui';
import { salesforceClient } from '../lib/salesforce';
import { metGetelde } from '../lib/utils';
import { isWerkbank } from '../lib/uiFlag';

/** Werkbank-render (plan 2026-09-02) of legacy; per toestel, zie lib/uiFlag. */
const WB = isWerkbank();

// Activity definitions
interface Activity {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  route: string;
  enabled: boolean;
  color: string;
}

const ACTIVITIES: Activity[] = [
  {
    id: 'route-rijden',
    name: 'Route rijden',
    description: 'Ophalen & afleveren',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
      </svg>
    ),
    route: '/routes',
    enabled: true,
    color: 'bg-kleda-primary',
  },
  {
    id: 'voorinspectie',
    name: 'Voorinspectie',
    description: 'Items beoordelen',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    route: '/voorinspectie',
    enabled: true,
    color: 'bg-amber-500',
  },
  {
    id: 'reinigen',
    name: 'Reinigen',
    description: 'Wasserij taken',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
      </svg>
    ),
    route: '/reinigen',
    enabled: true,
    color: 'bg-blue-500',
  },
  {
    id: 'foto-tijdens',
    name: 'Foto Tijdens',
    description: 'Tijdens het wassen',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    route: '/foto-tijdens',
    enabled: true,
    color: 'bg-cyan-600',
  },
  {
    id: 'na-inspectie',
    name: 'Na-inspectie',
    description: 'Kwaliteitscontrole',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    route: '/na-inspectie',
    enabled: true,
    color: 'bg-green-500',
  },
  {
    id: 'ondertapijt',
    name: 'Ondertapijt',
    description: 'Ondertapijt maken',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
    route: '/underlay',
    enabled: true,
    color: 'bg-purple-500',
  },
  {
    id: 'routes-klaarleggen',
    name: 'Routes klaarleggen',
    description: 'Voorbereiden',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    route: '/preparation',
    enabled: true,
    color: 'bg-orange-500',
  },
  {
    id: 'bus-inpakken',
    name: 'Bus inpakken',
    description: 'Laden voor route',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    route: '/bus-loading',
    enabled: true,
    color: 'bg-blue-500',
  },
  {
    id: 'zelf-halen-brengen',
    name: 'Zelf halen & brengen',
    description: 'Klant haalt/brengt zelf',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
    route: '/self-service',
    enabled: true,
    color: 'bg-teal-500',
  },
  {
    id: 'inpakken',
    name: 'Webshop',
    description: 'Orders inpakken',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    route: '/inpakken',
    enabled: true,
    color: 'bg-amber-600',
  },
  {
    id: 'berichten',
    name: 'Berichten',
    description: 'Bericht aan kantoor',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    route: '/berichten',
    enabled: true,
    color: 'bg-indigo-500',
  },
];

// Activity counts state type
interface ActivityCounts {
  voorinspectie: number | null;
  reinigen: number | null;
  fotoTijdens: number | null; // reinigen ('Klaar voor Reinigen') + na-inspectie (Drogen t/m In Reparatie)
  naInspectie: number | null;
  ondertapijt: number | null;
  routesKlaarleggen: number | null;
  busInpakken: number | null;
  zelfHalenBrengen: number | null;
  inpakken: number | null;
}

export function DashboardPage() {
  const { user } = useAuthStore();
  const {
    counts: berichtenCounts,
    startPolling: startBerichtenPolling,
    stopPolling: stopBerichtenPolling,
  } = useBerichtenStore();
  const [stats, setStats] = useState({
    pendingStops: 0,
    completedStops: 0,
    totalStops: 0,
    isLoading: true,
  });
  // Sterretjes + social-clips van vandaag (tegel "Social"); stil falen, puur informatief.
  const [socialVandaag, setSocialVandaag] = useState(0);
  const [activityCounts, setActivityCounts] = useState<ActivityCounts>({
    voorinspectie: null,
    reinigen: null,
    fotoTijdens: null,
    naInspectie: null,
    ondertapijt: null,
    routesKlaarleggen: null,
    busInpakken: null,
    zelfHalenBrengen: null,
    inpakken: null,
  });

  // Get current date in Dutch format
  const today = new Date();
  const dateFormatter = new Intl.DateTimeFormat('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
  const formattedDate = dateFormatter.format(today);

  // Fetch today's stats from the routes API
  useEffect(() => {
    let cancelled = false;
    async function laadSocial() {
      const { staff, pin } = useAuthStore.getState();
      if (!staff?.id || !pin) return;
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      const sinds = encodeURIComponent(d.toISOString());
      const headers = { 'X-Staff-Id': staff.id, 'X-Staff-Pin': pin };
      try {
        const [a, b] = await Promise.all([
          fetch(`/api/media?sinds=${sinds}&fase=social`, { headers }),
          fetch(`/api/media?sinds=${sinds}&kandidaat=true`, { headers }),
        ]);
        const ids = new Set<string>();
        for (const res of [a, b]) {
          if (!res.ok) continue;
          const data = (await res.json()) as { success: boolean; media: Array<{ id: string }> };
          if (data.success) data.media.forEach((m) => ids.add(m.id));
        }
        if (!cancelled) setSocialVandaag(ids.size);
      } catch {
        // stil
      }
    }
    void laadSocial();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    async function fetchStats() {
      try {
        const todayStr = new Date().toISOString().split('T')[0]!;
        const response = await salesforceClient.getDriverRoutes(todayStr);

        if (response.success && response.data) {
          // Calculate totals from all routes
          let totalStops = 0;
          let completedStops = 0;

          // The data is an array of routes with inline stops
          const routes = response.data as unknown as Array<{
            totalStops?: number;
            completedStops?: number;
            stops?: Array<{ Status__c?: string }>;
          }>;

          for (const route of routes) {
            const r = metGetelde(route);
            totalStops += r.totalStops || 0;
            completedStops += r.completedStops || 0;
          }

          setStats({
            totalStops,
            completedStops,
            pendingStops: totalStops - completedStops,
            isLoading: false,
          });
        } else {
          setStats(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        setStats(prev => ({ ...prev, isLoading: false }));
      }
    }

    fetchStats();
  }, []);

  // Berichten count polling (60s) — voedt de Berichten-tegel badge.
  useEffect(() => {
    startBerichtenPolling(60_000);
    return () => stopBerichtenPolling();
  }, [startBerichtenPolling, stopBerichtenPolling]);

  // Fetch all activity counts
  useEffect(() => {
    async function fetchActivityCounts() {
      const todayStr = new Date().toISOString().split('T')[0]!;

      // Fetch all counts in parallel
      const [
        voorinspectieRes,
        reinigenRes,
        naInspectieRes,
        ondertapijt,
        routesKlaarleggenRes,
        busInpakkenRes,
        selfServiceRes,
        inpakkenRes,
      ] = await Promise.allSettled([
        salesforceClient.getVoorinspectieItems(),
        salesforceClient.getReinigenItems(),
        salesforceClient.getNaInspectieItems(),
        salesforceClient.getUnderlayItems(),
        salesforceClient.getPreparationRoutes(todayStr),
        salesforceClient.getBusLoadingRoutes(todayStr),
        salesforceClient.getSelfServiceWorkOrders('ophalen'),
        salesforceClient.getInpakkenOrders(),
      ]);

      setActivityCounts({
        voorinspectie:
          voorinspectieRes.status === 'fulfilled' && voorinspectieRes.value.success
            ? voorinspectieRes.value.data?.items?.length ?? null
            : null,
        reinigen:
          reinigenRes.status === 'fulfilled' && reinigenRes.value.success
            ? reinigenRes.value.data?.items?.length ?? null
            : null,
        fotoTijdens:
          naInspectieRes.status === 'fulfilled' &&
          naInspectieRes.value.success &&
          reinigenRes.status === 'fulfilled' &&
          reinigenRes.value.success
            ? (naInspectieRes.value.data?.items?.length ?? 0) +
              (reinigenRes.value.data?.items?.length ?? 0)
            : null,
        naInspectie:
          naInspectieRes.status === 'fulfilled' && naInspectieRes.value.success
            ? naInspectieRes.value.data?.items?.length ?? null
            : null,
        ondertapijt:
          ondertapijt.status === 'fulfilled' && ondertapijt.value.success
            ? ondertapijt.value.data?.items?.length ?? null
            : null,
        routesKlaarleggen:
          routesKlaarleggenRes.status === 'fulfilled' && routesKlaarleggenRes.value.success
            ? routesKlaarleggenRes.value.data?.routes?.reduce((sum, r) => sum + (r.totalItems - r.preparedItems), 0) ?? null
            : null,
        busInpakken:
          busInpakkenRes.status === 'fulfilled' && busInpakkenRes.value.success
            ? busInpakkenRes.value.data?.routes?.reduce((sum, r) => sum + (r.totalItems - r.loadedItems), 0) ?? null
            : null,
        zelfHalenBrengen:
          selfServiceRes.status === 'fulfilled' && selfServiceRes.value.success
            ? selfServiceRes.value.data?.workOrders?.length ?? null
            : null,
        inpakken:
          inpakkenRes.status === 'fulfilled' && inpakkenRes.value.success
            ? inpakkenRes.value.data?.orders?.length ?? null
            : null,
      });
    }

    fetchActivityCounts();
  }, []);

  const badgeVoor = (id: string): number | null | undefined => {
    switch (id) {
      case 'voorinspectie': return activityCounts.voorinspectie;
      case 'reinigen': return activityCounts.reinigen;
      case 'foto-tijdens': return activityCounts.fotoTijdens;
      case 'na-inspectie': return activityCounts.naInspectie;
      case 'ondertapijt': return activityCounts.ondertapijt;
      case 'routes-klaarleggen': return activityCounts.routesKlaarleggen;
      case 'bus-inpakken': return activityCounts.busInpakken;
      case 'zelf-halen-brengen': return activityCounts.zelfHalenBrengen;
      case 'inpakken': return activityCounts.inpakken;
      case 'berichten': return berichtenCounts.open;
      default: return undefined;
    }
  };

  // Werkbank: tegels op één inktkleur met 2 px rand, badge als mono-getal,
  // "Vandaag" als mintbalk (NextStopBar-stijl), Kleed zoeken + Social als halve tegels.
  if (WB) {
    return (
      <div className="space-y-4">
        <section>
          <h2 className="font-wb-heading text-2xl font-bold text-wb-inkt leading-tight">
            Hallo, {user?.name?.split(' ')[0] || 'daar'}
          </h2>
          <p className="text-wb-inkt2 capitalize">{formattedDate}</p>
        </section>

        {/* Alleen als je vandaag een route rijdt: een wasserij-medewerker heeft
            niets aan "0 stops, geen stops". Tijdens het laden ook niet, anders
            springt de pagina voor iedereen zonder route. */}
        {!stats.isLoading && stats.totalStops > 0 && (
        <Link
          to="/routes"
          className="wb-focus flex items-center gap-3 min-h-[64px] px-3 rounded-wb-lg bg-wb-mint text-wb-mintink border-wb border-wb-mintink"
        >
          <span className="font-mono text-3xl font-semibold tabular-nums">{stats.isLoading ? '…' : stats.pendingStops}</span>
          <span className="flex-1 min-w-0">
            <span className="block text-[11px] font-bold uppercase tracking-wide">Vandaag</span>
            <span className="block font-wb-heading font-bold text-base leading-tight">
              stops te gaan{stats.completedStops > 0 ? ` · ${stats.completedStops} voltooid` : ''}
            </span>
          </span>
          <span className="font-mono text-sm">{stats.isLoading ? '' : `${stats.completedStops}/${stats.totalStops}`}</span>
          <svg className="w-5 h-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
        </Link>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Link to="/zoek" className="wb-focus min-h-tap flex items-center justify-center gap-2 px-3 rounded-wb bg-wb-vlak border-wb border-wb-rand font-wb-heading font-bold text-sm uppercase tracking-wide text-wb-inkt">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Kleed zoeken
          </Link>
          <Link to="/social" className="wb-focus min-h-tap flex items-center justify-center gap-2 px-3 rounded-wb bg-wb-vlak border-wb border-wb-rand font-wb-heading font-bold text-sm uppercase tracking-wide text-wb-inkt">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Social{socialVandaag > 0 ? <span className="font-mono">{socialVandaag}</span> : null}
          </Link>
        </div>

        <section>
          <h3 className="font-wb-heading font-bold text-xs uppercase tracking-wide text-wb-inkt2 mb-2">Wat wil je doen?</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {ACTIVITIES.map((activity) => {
              const badge = badgeVoor(activity.id);
              return (
                <Link
                  key={activity.id}
                  to={activity.route}
                  className="wb-focus relative min-h-[96px] p-3 rounded-wb bg-wb-vlak border-wb border-wb-rand text-wb-inkt flex flex-col justify-between"
                >
                  {badge != null && badge > 0 && (
                    <span className="absolute top-2 right-2 min-w-[28px] h-7 px-1.5 rounded-wb bg-wb-inkt text-white font-mono text-sm font-semibold flex items-center justify-center">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                  <span className="w-8 h-8 [&>svg]:w-8 [&>svg]:h-8">{activity.icon}</span>
                  <span>
                    <span className="block font-wb-heading font-bold text-base leading-tight">{activity.name}</span>
                    <span className="block text-xs text-wb-inkt2">{activity.description}</span>
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <p className="text-center font-mono text-xs text-wb-inkt3">Kleda Field v0.2.0 · werkbank</p>
      </div>
    );
  }

  return (
    <div className="space-y-lg">
      {/* Welcome section */}
      <section>
        <h2 className="font-heading text-2xl font-bold text-kleda-text-main mb-xs">
          Hallo, {user?.name?.split(' ')[0] || 'daar'}!
        </h2>
        <p className="text-kleda-text-muted capitalize">{formattedDate}</p>
      </section>

      {/* Daily stats card */}
      <Card variant="elevated">
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm text-kleda-text-muted mb-xs">Vandaag</p>
              <div className="flex items-baseline gap-sm">
                <span className="text-3xl font-bold text-kleda-primary">
                  {stats.isLoading ? '...' : stats.pendingStops}
                </span>
                <span className="text-kleda-text-muted">
                  {stats.totalStops === 0 && !stats.isLoading ? 'geen stops' : 'stops te gaan'}
                </span>
              </div>
              {stats.completedStops > 0 && (
                <p className="text-sm text-green-600 mt-xs">
                  ✓ {stats.completedStops} voltooid
                </p>
              )}
            </div>

            {/* Progress circle */}
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  className="text-gray-200"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  stroke="currentColor"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${(stats.completedStops / (stats.totalStops || 1)) * 176} 176`}
                  strokeLinecap="round"
                  className="text-kleda-primary transition-all duration-500"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-kleda-text-main">
                  {stats.isLoading ? '...' : `${stats.completedStops}/${stats.totalStops}`}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kleed zoeken — geen activiteit maar een hulpmiddel, dus bewust buiten
          het activiteitenraster en boven de lijsten: je hebt het nodig op het
          moment dat een kleed níét in je lijst staat. */}
      <Link to="/zoek" className="block">
        <Card variant="elevated" interactive className="cursor-pointer">
          <CardContent className="py-md">
            <div className="flex items-center gap-md">
              <div className="w-10 h-10 rounded-full bg-kleda-bg-clean flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-kleda-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-kleda-text-main">Kleed zoeken</p>
                <p className="text-sm text-kleda-text-muted">
                  Kleed niet in je lijst? Zoek op WI-nummer
                </p>
              </div>
              <svg className="w-5 h-5 text-kleda-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* Social — film een mooi kleed voor Instagram (plan 2026-09-01). Naast
          Kleed zoeken: ook een hulpmiddel, geen station. */}
      <Link to="/social" className="block">
        <Card variant="elevated" interactive className="cursor-pointer">
          <CardContent className="py-md">
            <div className="flex items-center gap-md">
              <div className="w-10 h-10 rounded-full bg-kleda-bg-clean flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-kleda-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-kleda-text-main">Social</p>
                <p className="text-sm text-kleda-text-muted">
                  Mooi kleed? Film het voor Instagram
                </p>
              </div>
              {socialVandaag > 0 && (
                <Badge variant="success" size="sm">{socialVandaag} vandaag</Badge>
              )}
              <svg className="w-5 h-5 text-kleda-text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </CardContent>
        </Card>
      </Link>

      {/* Activity grid - Picnic style */}
      <section>
        <h3 className="font-heading font-bold text-lg text-kleda-text-main mb-md">
          Wat wil je doen?
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-md">
          {ACTIVITIES.map((activity) => {
            return (
              <ActivityCard
                key={activity.id}
                activity={activity}
                badgeCount={badgeVoor(activity.id)}
              />
            );
          })}
        </div>
      </section>

      {/* Offline status */}
      <Card variant="outlined">
        <CardContent>
          <div className="flex items-center gap-md">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-kleda-text-main">Offline beschikbaar</p>
              <p className="text-sm text-kleda-text-muted truncate">
                Wijzigingen worden automatisch gesynchroniseerd
              </p>
            </div>
            <Badge variant="success" size="sm">Actief</Badge>
          </div>
        </CardContent>
      </Card>

      {/* App version */}
      <div className="text-center text-sm text-kleda-text-muted">
        <p>Kleda Field v0.2.0</p>
      </div>
    </div>
  );
}

interface ActivityCardProps {
  activity: Activity;
  badgeCount?: number | null;
}

function ActivityCard({ activity, badgeCount }: ActivityCardProps) {
  const cardContent = (
    <Card
      className={`h-full transition-all duration-200 ${
        activity.enabled
          ? 'hover:shadow-kleda-lg hover:-translate-y-0.5 active:scale-[0.98]'
          : 'opacity-60'
      }`}
    >
      <CardContent className="p-md relative">
        {/* Coming soon badge */}
        {!activity.enabled && (
          <Badge
            variant="default"
            size="sm"
            className="absolute top-2 right-2 text-[10px]"
          >
            Binnenkort
          </Badge>
        )}

        {/* Count badge for enabled activities */}
        {activity.enabled && badgeCount != null && badgeCount > 0 && (
          <div className="absolute top-2 right-2 min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {badgeCount > 99 ? '99+' : badgeCount}
          </div>
        )}

        {/* Icon */}
          <div
            className={`w-14 h-14 rounded-kleda-md flex items-center justify-center mb-sm ${
              activity.enabled
                ? `${activity.color} text-white`
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            {activity.icon}
          </div>

          {/* Text */}
          <h4 className="font-heading font-bold text-kleda-text-main text-sm leading-tight">
            {activity.name}
          </h4>
          <p className="text-xs text-kleda-text-muted mt-0.5">
            {activity.description}
          </p>

          {/* Arrow for enabled activities */}
          {activity.enabled && (
            <div className="absolute bottom-3 right-3">
              <svg
                className="w-5 h-5 text-kleda-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          )}
        </CardContent>
      </Card>
    );

  if (activity.enabled) {
    return (
      <Link to={activity.route} className="block">
        {cardContent}
      </Link>
    );
  }

  return (
    <div className="cursor-not-allowed">
      {cardContent}
    </div>
  );
}
